<template>
  <div class="admin-topbar">
    <div class="admin-topbar-brand">
      <i class="ti ti-device-tv"></i> goplayTv Admin
    </div>
    <div class="admin-topbar-actions">
      <RouterLink to="/" target="_blank" class="admin-topbar-link">
        <i class="ti ti-world"></i> View Site
      </RouterLink>
      <button class="admin-topbar-link" @click="handleLogout">
        <i class="ti ti-logout"></i> Logout
      </button>
    </div>
  </div>

  <div class="admin-layout">
    <aside class="admin-sidebar">
      <div class="admin-sidebar-brand">
        <i class="ti ti-device-tv" style="color:#c90e0e;font-size:1.3rem"></i>
        <span>Admin Panel</span>
      </div>

      <nav class="admin-sidebar-nav">
        <RouterLink
          v-for="item in navItems"
          :key="item.label"
          :to="item.to"
          class="admin-nav-item"
          active-class=""
          exact-active-class=""
          :class="{ active: isActive(item) }"
        >
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </RouterLink>
      </nav>

      <button class="admin-nav-item admin-nav-logout mt-auto" @click="handleLogout">
        <i class="ti ti-logout"></i>
        <span>Logout</span>
      </button>
    </aside>

    <main class="admin-main">
      <slot />
    </main>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthStore } from "@/store/auth";

const navItems = [
  { label: "Overview",    icon: "ti ti-layout-dashboard", to: "/admin",               tab: "" },
  { label: "Users",       icon: "ti ti-users",            to: "/admin?tab=users",     tab: "users" },
  { label: "Emails",      icon: "ti ti-mail",             to: "/admin?tab=emails",    tab: "emails" },
  { label: "Payments",    icon: "ti ti-credit-card",      to: "/admin?tab=payments",  tab: "payments" },
  { label: "Packages",    icon: "ti ti-package",          to: "/admin?tab=packages",  tab: "packages" },
  { label: "IPTV Panel",  icon: "ti ti-server",           to: "/admin?tab=panel",     tab: "panel" },
  { label: "Channels",    icon: "ti ti-device-tv",        to: "/admin?tab=channels",  tab: "channels" },
  { label: "Blog",        icon: "ti ti-pencil",           to: "/admin/blog",           tab: "" },
  { label: "Website CMS", icon: "ti ti-layout-grid",      to: "/admin/content",        tab: "" },
  { label: "Media",       icon: "ti ti-photo",            to: "/admin?tab=media",      tab: "media" },
  { label: "Analytics",   icon: "ti ti-chart-bar",        to: "/admin?tab=analytics", tab: "analytics" },
  { label: "Settings",    icon: "ti ti-settings",         to: "/admin?tab=settings",   tab: "settings" },
  { label: "Sales Guide", icon: "ti ti-book",             to: "/admin?tab=guide",      tab: "guide" },
];

export default defineComponent({
  name: "AdminLayout",
  setup() {
    const route = useRoute();
    const router = useRouter();
    const authStore = useAuthStore();

    function isActive(item: typeof navItems[0]): boolean {
      if (item.to === "/admin/blog") {
        return route.path.startsWith("/admin/blog");
      }
      if (item.to === "/admin/content") {
        return route.path.startsWith("/admin/content");
      }
      if (item.tab) {
        return route.path === "/admin" && route.query.tab === item.tab;
      }
      // Overview: active when on /admin with no tab or tab=overview
      return route.path === "/admin" && !route.query.tab;
    }

    async function handleLogout() {
      await authStore.logout();
      router.push("/login");
    }

    return { navItems, isActive, handleLogout };
  },
});
</script>

<style scoped>
/* Topbar */
.admin-topbar {
  position: fixed;
  top: 0; left: 0; right: 0;
  height: 52px;
  background: #201516;
  border-bottom: 1px solid #3a2526;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1.5rem;
  z-index: 1000;
}
.admin-topbar-brand {
  display: flex; align-items: center; gap: 0.5rem;
  color: #fff; font-weight: 800; font-size: 0.95rem;
}
.admin-topbar-brand i { color: #c90e0e; font-size: 1.2rem; }
.admin-topbar-actions { display: flex; align-items: center; gap: 0.5rem; }
.admin-topbar-link {
  display: flex; align-items: center; gap: 0.35rem;
  background: transparent; border: none;
  color: #c0c0c0; font-size: 0.85rem; font-family: inherit;
  cursor: pointer; padding: 0.3rem 0.6rem;
  border-radius: 6px; text-decoration: none; transition: all 0.2s;
}
.admin-topbar-link:hover { background: #2b1e1f; color: #fff; }

/* Layout */
.admin-layout {
  display: flex;
  min-height: 100vh;
  padding-top: 52px;
  background: #0a0102;
}

/* Sidebar */
.admin-sidebar {
  width: 220px;
  flex-shrink: 0;
  background: #201516;
  border-right: 1px solid #3a2526;
  display: flex;
  flex-direction: column;
  padding: 1.5rem 1rem;
  position: sticky;
  top: 52px;
  height: calc(100vh - 52px);
  overflow-y: auto;
}

.admin-sidebar-brand {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  color: #fff;
  font-weight: 800;
  font-size: 0.95rem;
  margin-bottom: 1.75rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #3a2526;
}

.admin-sidebar-nav {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.admin-nav-item {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.6rem 1rem;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #c0c0c0;
  cursor: pointer;
  font-size: 0.9rem;
  font-family: inherit;
  text-align: left;
  text-decoration: none;
  transition: all 0.2s;
  white-space: nowrap;
}
.admin-nav-item i { font-size: 1.05rem; flex-shrink: 0; }
.admin-nav-item:hover { background: #2b1e1f; color: #fff; }
.admin-nav-item.active { background: #c90e0e; color: #fff; }

.admin-nav-logout {
  color: #ff6b6b;
  margin-top: auto;
  width: 100%;
}
.admin-nav-logout:hover { background: rgba(201,14,14,0.2); color: #ff6b6b; }

/* Main */
.admin-main {
  flex: 1;
  padding: 2.5rem 2rem;
  min-width: 0;
}

/* Responsive */
@media (max-width: 768px) {
  .admin-layout { flex-direction: column; }
  .admin-sidebar {
    width: 100%;
    height: auto;
    position: static;
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0.75rem;
    gap: 0.25rem;
  }
  .admin-sidebar-brand { display: none; }
  .admin-sidebar-nav { flex-direction: row; flex-wrap: wrap; }
  .admin-nav-logout { margin-top: 0; width: auto; }
  .admin-main { padding: 1.5rem 1rem; }
}
</style>
